import { Typography, SelectOption, withForm } from '@stern-ui/ui';
import { useStore } from '@tanstack/react-form';
import { useEffect } from 'react';

import { useFetchStudiesQuery } from '@/components';

import { useFetchSearchCriteriaQuery, useFetchHciListsFilterQuery } from '../../../api';
import {
	generateSearchCriteriaOptions,
	generateStudySelectOptions,
	generateHciListOptions,
	renderHciNameStatus,
} from '../../../utils';

import { patientListFiltersFormOptions } from './patientListFiltersFormOptions';
import { PatientListFiltersFormSection } from './PatientListFiltersFormSection';

export const PatientListFiltersGeneral = withForm({
	...patientListFiltersFormOptions,
	render: function Render({ form }) {
		// get data to populate select options
		const { data: studies, isSuccess: studiesIsSuccess } = useFetchStudiesQuery({});
		const { data: searchCriteria, isSuccess: searchCriteriaIsSuccess } =
			useFetchSearchCriteriaQuery();
		const { data: hciList, isSuccess: hciListIsSuccess } = useFetchHciListsFilterQuery({});
		const studySelectOptions =
			studiesIsSuccess && studies?.results
				? generateStudySelectOptions(studies.results, true)
				: ([] as SelectOption[]);
		const searchCriteriaSelectOptions =
			searchCriteriaIsSuccess && searchCriteria?.results
				? generateSearchCriteriaOptions(searchCriteria.results)
				: ([] as SelectOption[]);
		const hciSelectOptions =
			hciListIsSuccess && hciList?.results
				? generateHciListOptions(hciList.results)
				: ([] as SelectOption[]);

		const studyId = useStore(form.store, (state) => state.values.study_id);

		useEffect(() => {
			// patient status can only be set when a single study is selected, so reset value if multiple/no studies selected
			if ((studyId || []).length !== 1) {
				form.setFieldValue('status', []);
			}
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, [studyId]);

		const customLabel = (data: SelectOption) => {
			return renderHciNameStatus(`${data.label}`, !!data.isActive);
		};

		return (
			<PatientListFiltersFormSection
				title='General'
				dataTestId='filters-general-section'
				isFirstSection={true}
			>
				<>
					<Typography className='text-secondaryTextDark'>
						Select multiple studies to return patients in each study (plus any patients
						in both studies).
					</Typography>
					<div className='grid grid-cols md:grid-cols-3 gap-4 md:gap-8 mt-6 pb-5'>
						<form.AppField name='study_id'>
							{(field) => (
								<field.SelectField
									label='Study'
									options={studySelectOptions}
									isMulti={true}
								/>
							)}
						</form.AppField>
						<form.AppField name='criteria_include'>
							{(field) => (
								<field.SelectField
									label='Have tagged search criteria'
									options={searchCriteriaSelectOptions}
									isMulti={true}
								/>
							)}
						</form.AppField>
						<form.AppField name='criteria_exclude'>
							{(field) => (
								<field.SelectField
									label='Don’t have tagged search criteria'
									options={searchCriteriaSelectOptions}
									isMulti={true}
								/>
							)}
						</form.AppField>
						<form.AppField name='hci_id'>
							{(field) => (
								<field.SelectField
									label='HCI'
									options={hciSelectOptions}
									isMulti={true}
									customLabel={customLabel}
								/>
							)}
						</form.AppField>
					</div>
				</>
			</PatientListFiltersFormSection>
		);
	},
});
